import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Loader2, X } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../../api";
import { reportSwallowed } from "../../lib/reportSwallowed";
import { useUiStore } from "../../stores/uiStore";
import { qk } from "../../hooks/queries";
import type { BulkErrorRow } from "../../types/contract";
import { Modal } from "../../ui/Modal";

// Everything rendered comes off the error row itself NEVER a session fetch: rows outlive the
// 30/90-day session prune, and the drop headers are back-filled onto the rows at terminal
// (`session_summary`) for exactly that reason. Paging is keyset with an OPAQUE cursor (base64url —
// parsing or rebuilding it corrupts it; BE-Light's note: the raw form contains `+00:00`, and `+`
// decodes to a space in a query string).

/* * Drop-header text from the back-filled summary; defensive — the summary is jsonb and null mid-run. */
export function sessionHeaderText(rows: BulkErrorRow[]): string {
  const s = rows.find((r) => r.session_summary)?.session_summary;
  if (s && typeof s === "object") {
    const files =
      (s as Record<string, unknown>).file_count ??
      (s as Record<string, unknown>).files;
    const imported =
      (s as Record<string, unknown>).ingested_count ??
      (s as Record<string, unknown>).imported;
    if (typeof files === "number") {
      const parts = [`${files} file${files === 1 ? "" : "s"}`];
      if (typeof imported === "number") parts.push(`${imported} imported`);
      parts.push(`${rows.length} error${rows.length === 1 ? "" : "s"}`);
      return parts.join(" · ");
    }
  }
  const when = rows[0]?.created_at?.slice(0, 10);
  return `${when ? `Drop on ${when}` : "A drop"} · ${rows.length} error${rows.length === 1 ? "" : "s"}`;
}

/* * Group rows by drop (newest first), and within a drop hang cascade children under their causal parent. */
export function groupErrors(rows: BulkErrorRow[]): {
  session: string;
  header: string;
  roots: { row: BulkErrorRow; children: BulkErrorRow[] }[];
}[] {
  const bySession = new Map<string, BulkErrorRow[]>();
  for (const r of rows) {
    const key = r.session_id ?? `no-session:${r.error_id}`;
    const list = bySession.get(key);
    if (list) list.push(r);
    else bySession.set(key, [r]);
  }
  return [...bySession.entries()].map(([session, group]) => {
    const ids = new Set(group.map((r) => r.error_id));
    const children = new Map<string, BulkErrorRow[]>();
    const roots: BulkErrorRow[] = [];
    for (const r of group) {
      // a parent outside the loaded page still renders its child as a root rather than losing it
      if (r.causal_parent_id && ids.has(r.causal_parent_id)) {
        const l = children.get(r.causal_parent_id);
        if (l) l.push(r);
        else children.set(r.causal_parent_id, [r]);
      } else roots.push(r);
    }
    return {
      session,
      header: sessionHeaderText(group),
      roots: roots.map((row) => ({
        row,
        children: children.get(row.error_id) ?? [],
      })),
    };
  });
}

function ErrorRowView({ r, indent }: { r: BulkErrorRow; indent?: boolean }) {
  return (
    <li className={`px-2 py-1 text-[11px] ${indent ? "pl-6" : ""}`}>
      <div className="flex items-start gap-1.5">
        <AlertTriangle size={11} className="mt-0.5 shrink-0 text-amber-500" />
        <div className="min-w-0 flex-1">
          <div className="text-fg">{r.message || r.reason_code}</div>
          <div className="mt-0.5 flex flex-wrap gap-x-2 text-[10px] text-faint">
            {r.filename && <span className="truncate">{r.filename}</span>}
            {r.object_name && <span className="truncate">{r.object_name}</span>}
            <span className="rounded bg-raised px-1">{r.reason_code}</span>
            {r.created_at && (
              <span className="tabular-nums">{r.created_at.slice(0, 10)}</span>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}

export function ErrorsMenu() {
  const open = useUiStore((s) => s.errorsMenu);
  const close = useUiStore((s) => s.closeErrorsMenu);
  const qc = useQueryClient();
  const [rows, setRows] = useState<BulkErrorRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [state, setState] = useState<"idle" | "loading" | "error">("idle");

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setState("loading");
    setRows([]);
    api
      .bulkErrors({ limit: 100 })
      .then((r) => {
        if (!alive) return;
        setRows(r.rows);
        setCursor(r.next_cursor);
        setTotal(r.total);
        setState("idle");
      })
      .catch(() => alive && setState("error"));
    // Opening IS the read receipt: move the bookmark, then refresh the badge. Monotonic server-side, so a
    // racing double-open can't move it backwards and resurface read errors.
    api
      .bulkErrorsMarkSeen()
      .then(() => qc.invalidateQueries({ queryKey: qk.bulkErrorsBadge }))
      .catch((err) => reportSwallowed("mark errors seen", err));
    return () => {
      alive = false;
    };
  }, [open, qc]);

  const groups = useMemo(() => groupErrors(rows), [rows]);

  if (!open) return null;

  const loadMore = async () => {
    if (!cursor) return;
    setState("loading");
    try {
      const r = await api.bulkErrors({ limit: 100, cursor });
      setRows((cur) => [...cur, ...r.rows]);
      setCursor(r.next_cursor);
      setTotal(r.total);
      setState("idle");
    } catch {
      setState("error");
    }
  };

  return (
    <Modal>
      <div
        className="fixed inset-0 z-panel flex items-center justify-center bg-black/40 p-4"
        onClick={close}
      >
        <div
          className="flex max-h-[85vh] w-full max-w-xl flex-col rounded-lg border border-line bg-surface shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-line px-3 py-2">
            <h2 className="flex items-center gap-1.5 text-sm font-semibold">
              <AlertTriangle size={14} className="text-amber-500" /> Errors
            </h2>
            <button
              className="rounded p-1 text-muted hover:bg-hover"
              onClick={close}
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-auto p-2">
            {state === "error" && rows.length === 0 ? (
              <p className="py-4 text-center text-[11px] text-red-400">
                Couldn’t load the error history. It hasn’t gone anywhere — try
                again in a moment.
              </p>
            ) : rows.length === 0 && state === "loading" ? (
              <p className="flex items-center justify-center gap-1.5 py-4 text-[11px] text-muted">
                <Loader2 size={12} className="animate-spin" /> Loading…
              </p>
            ) : rows.length === 0 ? (
              <p className="py-4 text-center text-[11px] text-faint">
                Nothing here — every drop so far imported cleanly.
              </p>
            ) : (
              <div className="space-y-2">
                {groups.map((g) => (
                  <div key={g.session} className="rounded border border-line">
                    <div className="border-b border-line px-2 py-1 text-[10px] font-medium text-muted">
                      {g.header}
                    </div>
                    <ul className="divide-y divide-line/50">
                      {g.roots.map(({ row, children }) => (
                        <div key={row.error_id}>
                          <ErrorRowView r={row} />
                          {children.map((c) => (
                            <ErrorRowView key={c.error_id} r={c} indent />
                          ))}
                        </div>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between border-t border-line px-3 py-1.5 text-[10px] text-faint">
            <span>
              {rows.length} of {total} — a permanent record; entries can’t be
              cleared
            </span>
            {cursor && (
              <button
                className="rounded border border-line px-2 py-0.5 text-[10px] text-muted hover:bg-hover"
                onClick={loadMore}
                disabled={state === "loading"}
              >
                {state === "loading" ? "Loading…" : "Load more"}
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
